import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
}

export default function LivingSpacesGallery() {
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering') // 'entering', 'visible', 'exiting'
  
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Function to parse image filename into alt text
  function parseImageFileName(filename: string): string {
    // Remove file extension
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    // Replace hyphens with spaces
    return withoutExtension.replace(/-/g, ' ')
  }

  // Function to fix path for production and development consistency
  function fixImagePath(path: string): string {
    // Remove /public prefix if it exists (for dev mode)
    let imagePath = path.replace('/public', '')
    
    // Ensure path starts with a slash
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    
    return imagePath
  }

  // Load images dynamically
  useEffect(() => {
    try {
      // Using Vite's import.meta.glob to get all images from the Other folder
      const imageModules = import.meta.glob('/public/assets/images/Other/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      // Convert the modules to ImageItem objects
      Object.keys(imageModules).forEach(path => {
        const imagePath = fixImagePath(path)
        
        // Get filename for alt text
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText
        })
      })

      setDisplayedImages(loadedImages)
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [])
  
  // Lightbox functions
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    // Start animation
    setTimeout(() => setLightboxAnimation('visible'), 50)
    // Prevent body scrolling when lightbox is open
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    // Start exit animation
    setLightboxAnimation('exiting')
    // Wait for animation to finish before fully closing
    setTimeout(() => {
      setLightboxOpen(false)
      // Restore body scrolling
      document.body.style.overflow = 'visible'
    }, 300) // Should match the animation duration
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  return (
    <main>
      <Helmet>
        <title>Outdoor Living Spaces Gallery | Busy Beaver Construction | Calgary</title>
        <meta name="description" content="Explore our outdoor living spaces gallery featuring custom outdoor kitchens, seating areas, fire features, and entertainment spaces designed for Calgary homes." />
        <meta name="keywords" content="outdoor living spaces, outdoor kitchens, outdoor seating areas, fire features, entertainment spaces, Calgary outdoor living, backyard transformation" />
        <link rel="canonical" href="https://busybeaverconstruction.com/gallery/living-spaces" />
        <meta property="og:title" content="Outdoor Living Spaces Gallery | Busy Beaver Construction" />
        <meta property="og:description" content="Browse our portfolio of custom outdoor living spaces designed to transform Calgary backyards into functional, beautiful extensions of your home." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/gallery/living-spaces" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Other/Living Spaces/Custom-cabinets-in-Cedar-to-accommodate-homeowners-BBQ-.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Other/Living Spaces/Custom-cabinets-in-Cedar-to-accommodate-homeowners-BBQ-.jpg" as="image" />
      </Helmet>
      <Header />
      
      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg"
          alt="Azek Vintage Walnut composite deck and outdoor living space by Busy Beaver Construction Calgary"
          className="absolute inset-0 w-full h-full object-cover object-top loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Transform Your Outdoor Space Into A Living Space</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Living Spaces Development Services</h1>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="space-y-6 text-base text-gray-500 leading-relaxed">
              <p>
                An outdoor living space is one that should allow you to extend your entertaining and living space from inside the home into the backyard. 
                We understand that the design of your outdoor living space should reflect your needs. It should be a functional space that offers both 
                privacy and comfortability for entertaining or relaxing.
              </p>
              
              <p>
                With more clients looking to take entertaining outside, we are becoming more creative in building outdoor living spaces.
              </p>
              
              <h3 className="text-xl font-bold mt-8 mb-4">Outdoor Living Spaces can include:</h3>
              
              <ul className="list-disc pl-12 space-y-4">
                <li>
                  <p><strong>Pergolas</strong> – Pergolas bring shade and privacy so homeowners can bring outdoor dining and entertainment into their backyard. 
                  They can be attached to your home, or detached to create shelter over an outdoor hot tub or seating area.</p>
                </li>
                
                <li>
                  <p><strong>Privacy Walls/Trellis</strong> – Privacy walls create additional privacy and add a wind block so your outdoor space can be 
                  enjoyed on those cooler evenings. Whether it be designed in wood or metal, a privacy wall or trellis creates a beautiful 
                  backdrop for outdoor living spaces.</p>
                </li>
                
                <li>
                  <p><strong>Benches/Planter Boxes</strong> – Built in benches and planter boxes are a great feature to define the perimeter of the deck. 
                  Benches can be used to substitute for a railing, and add extra seating to a deck without taking up added space. Benches and planter 
                  boxes are usually made with the same material as the deck surface. Whether they be built-in, or free standing, they give your outdoor 
                  space functionality, character and color.</p>
                </li>
                
                <li>
                  <p><strong>Lighting</strong> – Choosing the right outdoor lighting with help beautify your space, and makes is a useful gathering place 
                  any time of the day, year round. The outdoor lighting options are limitless, whether it be LED post lighting in railing, lighting in 
                  the deck surface, or accent lighting on the stairs. LED options come in several colors choices, where you have the option to change 
                  the color from your phone based on the mood you want to set.</p>
                </li>
                
                <li>
                  <p><strong>Outdoor Kitchens</strong> – A fully equipped outdoor living space with a kitchen, dining area and comfortable seating can be 
                  as perfect for large gatherings as it is for small. An outdoor kitchen can be something as simple as a BBQ with built-in cabinets, or 
                  as large of an investment as you would put into creating an indoor kitchen.</p>
                </li>
                
                <li>
                  <p><strong>Hot tubs/pools</strong> – The addition of a hot tub to your backyard can add years of enjoyment to your outdoor living space. 
                  A hot tub or swim spa can be integrated into your deck or landscape so it's functional and fits attractively into your design scheme.</p>
                </li>
              </ul>
            </div>
            
            <div className="space-y-6">
              <div className="overflow-hidden h-[460px] md:h-[550px]">
                <img 
                  src="/assets/images/2020/04/20190911_210311.jpg"
                  alt="Custom covered deck with integrated planter boxes and privacy features by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[300px]">
                <img 
                  src="/assets/images/2020/04/IMG_2332.jpg"
                  alt="Multi-level outdoor living space with integrated deck and seating area by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
              <div className="overflow-hidden h-[280px] md:h-[300px]">
                <img 
                  src="/assets/images//2020/04/IMG_2350.jpg"
                  alt="Custom deck with integrated water feature and accent lighting by Busy Beaver Construction Calgary"
                  className="w-full h-full object-cover loading-lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-8 md:mb-14">
            <h1 className="text-center text-5xl lg:text-6xl font-primary">Living Spaces Gallery</h1>
          </div>
          
          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[280px] md:h-[320px]">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-500">{image.alt}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* No results message */}
          {displayedImages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-700">Loading images...</p>
            </div>
          )}
          
          <div className="text-center mt-12">
            <p className="mb-6 text-gray-700">
              Interested in creating your own outdoor living space? Contact us to discuss your vision and get a free estimate.
            </p>
            <a
              href="/estimate" 
              className="inline-block px-6 py-3 bg-beaver-green text-black font-medium rounded hover:bg-beaver-green-dark hover:brightness-110 transition-all duration-300"
              onClick={(e) => handleNavigation(e, '/estimate')}
            >
              GET FREE ESTIMATE
            </a>
          </div>
        </div>
      </section>
      
      {/* WE DO DAM GOOD WORK Section */}
      <section className="py-10 bg-gray-900 text-white text-center">
        <h2 className="text-4xl md:text-5xl font-bold tracking-wide">
          WE DO <span className="text-red-600">DAM</span> GOOD WORK
        </h2>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-opacity duration-300 ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            {/* Close button */}
            <button 
              onClick={closeLightbox}
              className={`absolute top-4 right-4 z-10 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-full object-contain transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-90' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'
                }`}
              />
              
              {/* Image caption */}
              <div 
                className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white py-2 px-4 rounded text-center max-w-lg transition-all duration-300 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                {displayedImages[lightboxSlide].alt}
              </div>
              
              {/* Navigation buttons */}
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[0px] md:left-[60px] lg:left-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-2xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[0px] md:right-[60px] lg:right-[-180px] top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-beaver-green text-white p-3 rounded-full transition-all duration-300 focus:outline-none ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
} 